/**
 * A calm, governed journey composition (#1466 / ADR F-30).
 *
 * Setup uses a navigable `Steps` spine while delivery and CPQ use factual
 * `ProcessBar` rails. JourneyShell deliberately accepts either as its progress
 * slot instead of pretending those two controls mean the same thing. It adds
 * the shared anatomy around them: where the journey is now, what comes next,
 * what blocks it, and how to recover.
 *
 * This component fetches nothing and derives no authority from a stage or
 * status. Actions are React nodes supplied by the host after its governed door
 * has said the act is available. An absent action produces no control at all.
 */
import { useId, type ReactNode } from 'react'
import { PageShell, type PageWidth } from '../shell/PageShell'
import s from './JourneyShell.module.css'

export interface JourneyMoment {
  /** Short semantic zone label, for example "Now" or "Next step". */
  label: ReactNode
  title: ReactNode
  detail?: ReactNode
  meta?: ReactNode
  /** Already-governed host affordance. Absence means no control. */
  action?: ReactNode
}

export interface JourneyBlocker {
  title: ReactNode
  detail?: ReactNode
  code?: ReactNode
}

export interface JourneyShellProps {
  title: ReactNode
  headingLevel?: 1 | 2 | 3
  eyebrow?: ReactNode
  summary?: ReactNode
  status?: ReactNode
  width?: PageWidth
  /** A host-selected Steps or ProcessBar instance; JourneyShell does not reinterpret it. */
  progress: ReactNode
  currentStep: JourneyMoment
  nextStep?: JourneyMoment
  blocker?: JourneyBlocker
  /** Rendered only with a blocker. A recovery without a problem is misleading chrome. */
  recovery?: JourneyMoment
  testId?: string
  children?: ReactNode
}

function Moment({ moment, kind }: { moment: JourneyMoment; kind: 'current' | 'next' | 'recovery' }) {
  const generatedId = useId()
  const titleId = `journey-${kind}-${generatedId.replace(/:/g, '')}`
  return (
    <section className={s.moment} data-kind={kind} aria-labelledby={titleId}>
      <div className={s.momentCopy}>
        <div className={s.label}>{moment.label}</div>
        <h2 className={s.momentTitle} id={titleId}>{moment.title}</h2>
        {moment.detail != null && <div className={s.detail}>{moment.detail}</div>}
        {moment.meta != null && <div className={s.meta}>{moment.meta}</div>}
      </div>
      {moment.action != null && <div className={s.action}>{moment.action}</div>}
    </section>
  )
}

export function JourneyShell({
  title,
  headingLevel,
  eyebrow,
  summary,
  status,
  width = 'wide',
  progress,
  currentStep,
  nextStep,
  blocker,
  recovery,
  testId,
  children,
}: JourneyShellProps) {
  return (
    <PageShell
      title={title}
      headingLevel={headingLevel}
      eyebrow={eyebrow}
      summary={summary}
      status={status}
      width={width}
      testId={testId}
    >
      <div className={s.progress} data-journey-progress>{progress}</div>

      <div className={s.moments}>
        <Moment moment={currentStep} kind="current" />
        {nextStep != null && <Moment moment={nextStep} kind="next" />}
      </div>

      {blocker != null && (
        <section className={s.blocked} aria-label="Journey blocker" role="status">
          <div className={s.blockerCopy}>
            <div className={s.label}>Blocked</div>
            <h2 className={s.blockerTitle}>{blocker.title}</h2>
            {blocker.detail != null && <div className={s.detail}>{blocker.detail}</div>}
            {blocker.code != null && <div className={s.code}>{blocker.code}</div>}
          </div>
          {recovery != null && <Moment moment={recovery} kind="recovery" />}
        </section>
      )}

      {children != null && <div className={s.content}>{children}</div>}
    </PageShell>
  )
}
